"use client";

import trackClevertapEvents from "@/utils/trackClevertapEvents";
import { Button, CheckboxList } from "@monsterindia/global-ui-components";
import { useMemo, useState } from "react";

import { unSubscribeOutReachAPI } from "../query";
import {
  OutreachSubscriptionState,
  UnsubscribeFormData,
} from "../types/unsubscribe";
import { CompanyKeys } from "../types/unsubscribe";

interface ResubscribeCompaniesPageProps {
  setOutreachSubscriptionData?: React.Dispatch<
    React.SetStateAction<OutreachSubscriptionState>
  >;
  outreachSubscriptionData: OutreachSubscriptionState;
}

export const ResubscribeCompaniesPage: React.FC<
  ResubscribeCompaniesPageProps
> = ({ setOutreachSubscriptionData, outreachSubscriptionData }) => {
  const [companies, setCompanies] = useState<
    (CompanyKeys & { checked: boolean })[]
  >(
    outreachSubscriptionData?.currentUnsubscribeData?.data?.company_consents
      ?.filter((data: any) => data.subscribed === false)
      ?.map((data: any) => ({
        ...data,
        checked: false,
      })) || [],
  );
  const [resetCheckBoxes, setResetCheckBoxes] = useState(false);
  const [selectAll, setSelectAll] = useState({
    checked: false,
    trySelectingAll: true,
  });

  const total = companies.length;
  const selectedCount = useMemo(
    () => companies.filter((c) => c.checked).length,
    [companies],
  );

  const allSelected = selectedCount === total;
  const noneSelected = selectedCount === 0;
  const someSelected = !allSelected && !noneSelected;

  const eventChangeHandlerForSelectAll = (data: any) => {
    setSelectAll({
      ...selectAll,
      checked: !selectAll.checked,
      trySelectingAll: !(!selectAll.checked && (someSelected || allSelected)),
    });
    if (selectAll.checked) {
      setResetCheckBoxes(true);
    }
  };

  const handleCompanyToggle = (id: number, nextChecked: boolean) => {
    setCompanies((prev) =>
      prev.map((c) => (c.corp_id === id ? { ...c, checked: nextChecked } : c)),
    );
    setResetCheckBoxes(false);
  };

  const onClose = () => {
    if (setOutreachSubscriptionData) {
      setOutreachSubscriptionData((prevData: OutreachSubscriptionState) => ({
        ...prevData,
        resubscribeSuccess: false,
        resubscribeModalOpen: false,
      }));
    }
  };

  const onResubscribe = async () => {
    const selectedIds = companies
      .filter((c) => c.checked)
      .map((c) => {
        return {
          corp_id: c.corp_id,
        };
      });
    if (!selectedIds.length) return;
    const formData: UnsubscribeFormData = {
      action: "subscribe",
      scope: "company",
    };

    formData["companies"] = selectedIds;

    const response: any = await unSubscribeOutReachAPI(formData);
    const statusResponse = response.success ?? false;
    if (setOutreachSubscriptionData) {
      let currentUnsubscribeDataResp =
        outreachSubscriptionData?.currentUnsubscribeData?.data
          ?.company_consents || [];
      let currentUnsubscribeTemp = response?.data;
      if (currentUnsubscribeTemp && currentUnsubscribeTemp?.length > 0) {
        currentUnsubscribeDataResp = currentUnsubscribeDataResp.filter(
          (company: any) => {
            return (
              currentUnsubscribeTemp.findIndex(
                (tempCompany: any) => tempCompany.corp_id === company.corp_id,
              ) === -1
            );
          },
        );
      }
      setOutreachSubscriptionData((prevData: OutreachSubscriptionState) => ({
        ...prevData,
        resubscribeSuccess: statusResponse,
        resubscribeSuccessMessage: response.message || "",
        currentUnsubscribeData: {
          ...prevData?.currentUnsubscribeData,
          data: {
            ...prevData?.currentUnsubscribeData?.data,
            company_consents: currentUnsubscribeDataResp,
          },
        },
      }));
    }
    const eventProps = {
      ResubscribeType: "RecruiterLevel",
    };

    trackClevertapEvents.eventPush("Resubscribe Recruiter Comm", eventProps);
  };

  return (
    <div>
      <div className="p-4">
        <h1 className="text-base font-bold leading-6 text-black">
          List of unsubscribed companies ({total})
        </h1>
        <p className="mt-[6px] text-sm font-normal leading-5 text-[#59566C]">
          These are the companies you&apos;ve opted out of. Keep the checkbox
          active to resubscribe.
        </p>
      </div>

      <div className="mb-4 px-4 md:mb-2">
        {/* Select All */}
        <div className="mx-[-16px] mb-4 bg-[#F6F6F7] px-4 py-2">
          <CheckboxList
            className="relative bottom-[2px] text-sm font-medium leading-5 text-[#17142A]"
            defaultChecked={false}
            errorLabel="Something went wrong"
            reset={selectAll.checked && noneSelected}
            disabled={false}
            eventChangeHandler={(payload: any) =>
              eventChangeHandlerForSelectAll(payload)
            }
            label=""
            layout="row"
            options={[
              {
                displayName: "Select All",
                id: "select-all",
                value: "select-all",
              },
            ]}
            enableAutoSelect={someSelected || allSelected}
            allChecked={allSelected}
            size="large"
            checkedColor="#6E00BE"
          />
        </div>

        <div className="no-scrollbar grid max-h-[154px] grid-cols-1 gap-y-4 overflow-y-scroll md:max-h-[224px] md:grid-cols-2 md:gap-x-[172px]">
          {companies.map((c) => (
            <CheckboxList
              key={c.corp_id}
              className="text-sm font-normal leading-5 text-[#17142A]"
              defaultChecked={false}
              errorLabel="Something went wrong"
              reset={resetCheckBoxes}
              disabled={false}
              eventChangeHandler={(payload: any) => {
                const next = payload.values && payload.values.length > 0;
                handleCompanyToggle(c.corp_id, next);
              }}
              label=""
              layout="row"
              options={[
                {
                  displayName: c.company_name,
                  id: c.corp_id,
                  value: c.corp_id,
                } as any,
              ]}
              enableAutoSelect={selectAll.checked && selectAll.trySelectingAll}
              allChecked={true}
              size="large"
              checkedColor="#6E00BE"
            />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-4 rounded-b-[8px] bg-[#FFF] !px-[20px] py-4 shadow-[0_-4px_8px_-2px_rgba(0,0,0,0.08),0_-2px_2px_-2px_rgba(0,0,0,0.15)] md:px-4 md:py-2">
        <Button
          className="w-full md:!w-[68px]"
          label="Cancel"
          modifier="tertiary"
          onClick={onClose}
          size="md"
        />

        <Button
          className="w-full md:!w-[152px]"
          label="Re-Subscribe"
          modifier="primary"
          onClick={onResubscribe}
          size="md"
        />
      </div>
    </div>
  );
};
